import type { RequestPayload } from "@inertiajs/core";
import { usePage } from "@inertiajs/react";
import { Alert, Checkbox, Divider, Fieldset, Group, Stack } from "@mantine/core";
import { YearPickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { IconAlertTriangle } from "@tabler/icons-react";
import type { YearContext } from "../../models/yearContext";
import { SaveButton } from "../button/SaveButton";

type YearContextFormProps = {
  year?: YearContext;
  submitButtonLabel: string;
  onSubmit: (values: RequestPayload) => void;
};

type FormErrors = {
  starts_at?: string[];
  ends_at?: string[];
};

export function YearContextForm({ year, submitButtonLabel, onSubmit }: YearContextFormProps) {
  const { errors } = usePage<{ errors: FormErrors }>().props;

  const form = useForm({
    initialValues: {
      starts_at: year?.starts_at.toString(),
      ends_at: year?.ends_at.toString(),
      current: year?.current ?? false,
    },
  });

  form.watch("starts_at", (field) => field.value && form.setFieldValue("ends_at", (Number.parseInt(field.value, 10) + 1).toString()));

  const shouldShowWarning = form.getValues().current && !year?.current;

  return (
    <form onSubmit={form.onSubmit((values) => onSubmit(values))}>
      <Fieldset legend="Details">
        <Stack maw="60rem">
          <Group>
            <YearPickerInput
              flex={1}
              minDate={new Date()}
              maxDate={new Date(9998, 12)}
              label="Beginnt"
              key={form.key("starts_at")}
              {...form.getInputProps("starts_at")}
              error={errors?.starts_at}
            />

            <YearPickerInput
              disabled
              flex={1}
              label="Endet"
              key={form.key("ends_at")}
              {...form.getInputProps("ends_at")}
              error={errors?.ends_at}
            />
          </Group>
          <Checkbox
            disabled={year?.current}
            color="gray"
            label="Aktives Jahr"
            key={form.key("current")}
            {...form.getInputProps("current", { type: "checkbox" })}
          />
          {shouldShowWarning && (
            <Alert variant="light" icon={<IconAlertTriangle />} title="Aktives Jahr wird umgestellt" color="zli-orange">
              Dieses aktuelle Jahr wird für alle Benutzer aktiviert!
            </Alert>
          )}
          <Divider />
          <SaveButton label={submitButtonLabel} />
        </Stack>
      </Fieldset>
    </form>
  );
}
